import React, { useState, useEffect, useRef } from 'react'
import { leadsApi } from '../services/api'
import { supabase } from '../services/supabase'

// ── Datos Colombia ──────────────────────────────────────────────────────────

const DEPARTAMENTOS = [
  'Amazonas', 'Antioquia', 'Arauca', 'Atlántico', 'Bogotá D.C.',
  'Bolívar', 'Boyacá', 'Caldas', 'Caquetá', 'Casanare', 'Cauca',
  'Cesar', 'Chocó', 'Córdoba', 'Cundinamarca', 'Guainía', 'Guaviare',
  'Huila', 'La Guajira', 'Magdalena', 'Meta', 'Nariño',
  'Norte de Santander', 'Putumayo', 'Quindío', 'Risaralda',
  'San Andrés y Providencia', 'Santander', 'Sucre', 'Tolima',
  'Valle del Cauca', 'Vaupés', 'Vichada',
]

const TIPOS_DOCUMENTO = ['CC', 'CE', 'TI', 'NIT', 'Pasaporte']

const TIPOS_VIA = [
  { label: 'Calle (CL)',       value: 'CL' },
  { label: 'Carrera (KR)',     value: 'KR' },
  { label: 'Diagonal (DG)',    value: 'DG' },
  { label: 'Transversal (TV)', value: 'TV' },
  { label: 'Avenida (AV)',     value: 'AV' },
]

const CUADRANTES = ['', 'Norte', 'Sur', 'Este', 'Oeste', 'BIS']

const WHATSAPP_NUMBER = '573507835575'
const SESSION_KEY     = 'jenny_kids_producto'
const OAUTH_FLAG      = 'jenny_kids_oauth_pending'
// Evento personalizado que ProductCard dispara cuando guarda un producto
const PRODUCTO_EVENT  = 'jenny_kids_producto_set'

// ── Tipos ────────────────────────────────────────────────────────────────────

interface ProductoContext {
  nombre: string
  talla: string
  precio: number
}

interface QuickForm {
  name: string
  email: string
  phone: string
}

interface FormStep1 {
  name: string
  email: string
  phone: string
  tipo_documento: string
  numero_documento: string
}

interface FormStep2 {
  departamento: string
  municipio: string
  barrio: string
  tipo_via: string
  numero_via: string
  cuadrante: string
  numero_cruce: string
  numero_puerta: string
  complemento: string
}

const EMPTY_QUICK: QuickForm = { name: '', email: '', phone: '' }

const EMPTY_STEP1: FormStep1 = {
  name: '', email: '', phone: '', tipo_documento: 'CC', numero_documento: ''
}
const EMPTY_STEP2: FormStep2 = {
  departamento: '', municipio: '', barrio: '',
  tipo_via: 'CL', numero_via: '', cuadrante: '',
  numero_cruce: '', numero_puerta: '', complemento: ''
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const construirDireccion = (f: FormStep2): string => {
  if (!f.tipo_via || !f.numero_via || !f.numero_cruce || !f.numero_puerta) return ''
  const cuad = f.cuadrante ? ` ${f.cuadrante}` : ''
  const comp  = f.complemento ? ` ${f.complemento}` : ''
  return `${f.tipo_via} ${f.numero_via}${cuad} #${f.numero_cruce} - ${f.numero_puerta}${comp}`
}

const mensajeRapido = (form: QuickForm): string =>
  `📋 *CONSULTA JENNY KIDS*

👤 *MIS DATOS*
• Nombre: ${form.name}
• Celular: ${form.phone}
• Email: ${form.email}

Me registro para recibir información sobre sus productos y disponibilidad. ¡Gracias!`

const mensajePedido = (
  s1: FormStep1,
  s2: FormStep2,
  p: ProductoContext
): string => {
  const dir = construirDireccion(s2)
  const doc = s1.tipo_documento && s1.numero_documento
    ? `\n• Documento: ${s1.tipo_documento} ${s1.numero_documento}` : ''

  return `🛍️ *PEDIDO JENNY KIDS*

🛒 *PRODUCTO SELECCIONADO*
• Producto: ${p.nombre}
• Talla: ${p.talla}
• Precio: $${p.precio.toLocaleString('es-CO')}

👤 *MIS DATOS*
• Nombre: ${s1.name}${doc}
• Celular: ${s1.phone}
• Email: ${s1.email}

📍 *DIRECCIÓN DE ENVÍO*
• ${s2.departamento}${s2.municipio ? ', ' + s2.municipio : ''}
• Barrio: ${s2.barrio}
• Dirección: ${dir}

¿Hay disponibilidad de este producto en la talla indicada? ¡Gracias!`
}

const waUrl = (msg: string) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`

// ── Componente ───────────────────────────────────────────────────────────────

const ContactForm: React.FC = () => {
  const [step, setStep]           = useState<1 | 2 | 3>(1)
  const [quickForm, setQuickForm] = useState<QuickForm>(EMPTY_QUICK)
  const [step1, setStep1]         = useState<FormStep1>(EMPTY_STEP1)
  const [step2, setStep2]         = useState<FormStep2>(EMPTY_STEP2)
  const [errorsQ, setErrorsQ]     = useState<Partial<QuickForm>>({})
  const [errors1, setErrors1]     = useState<Partial<FormStep1>>({})
  const [errors2, setErrors2]     = useState<Partial<FormStep2>>({})
  const [isSaving, setIsSaving]   = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [googleLoading, setGoogleLoading]     = useState(false)
  const [googleUser, setGoogleUser]           = useState<{ email: string; name: string } | null>(null)
  const [showOAuthBanner, setShowOAuthBanner] = useState(false)
  const [producto, setProducto]               = useState<ProductoContext | null>(null)

  // Ref para hacer focus en el celular después del OAuth
  const phoneQuickRef = useRef<HTMLInputElement>(null)
  const phone1Ref     = useRef<HTMLInputElement>(null)

  // ── Leer producto desde sessionStorage ─────────────────────────────────────
  // Se ejecuta al montar Y cuando ProductCard dispara el evento PRODUCTO_EVENT
  useEffect(() => {
    const leerProducto = () => {
      const saved = sessionStorage.getItem(SESSION_KEY)
      if (saved) {
        try { setProducto(JSON.parse(saved)) } catch {}
        sessionStorage.removeItem(SESSION_KEY)
      }
    }
    leerProducto()
    window.addEventListener(PRODUCTO_EVENT, leerProducto)
    return () => window.removeEventListener(PRODUCTO_EVENT, leerProducto)
  }, [])

  // ── Detectar sesión Google (carga inicial + retorno OAuth) ─────────────────
  useEffect(() => {
    const aplicarSesion = (
      session: { user: { email?: string | null; user_metadata?: { full_name?: string } } } | null,
      mostrarBanner: boolean
    ) => {
      if (!session?.user) return
      const email = session.user.email || ''
      const name  = session.user.user_metadata?.full_name || ''
      setGoogleUser({ email, name })
      setQuickForm(prev => ({ ...prev, email: email || prev.email, name: name || prev.name }))
      setStep1(prev => ({ ...prev, email: email || prev.email, name: name || prev.name }))
      if (mostrarBanner) {
        setShowOAuthBanner(true)
        setTimeout(() => {
          document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })
          setTimeout(() => {
            phoneQuickRef.current?.focus()
            phone1Ref.current?.focus()
          }, 600)
        }, 300)
      }
    }

    // getSession corre justo al montar — aquí el flag todavía existe si venimos de OAuth
    // porque Supabase procesa el ?code= ANTES de que React monte (el flag ya está en localStorage)
    supabase.auth.getSession().then(({ data: { session } }) => {
      const cameFromOAuth = localStorage.getItem(OAUTH_FLAG) === '1'
      if (cameFromOAuth) localStorage.removeItem(OAUTH_FLAG)
      aplicarSesion(session, cameFromOAuth)
    })

    // onAuthStateChange como respaldo (a veces SIGNED_IN llega después de getSession)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        aplicarSesion(session, false) // el banner ya se activa desde getSession
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // ── Google OAuth ────────────────────────────────────────────────────────────

  const handleGoogleLogin = async () => {
    setGoogleLoading(true)
    // localStorage sobrevive el redirect en todos los navegadores (incluyendo Safari)
    localStorage.setItem(OAUTH_FLAG, '1')
    // Si hay un producto, preservarlo para que sobreviva el redirect
    if (producto) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(producto))
    }
    try {
      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + window.location.pathname,
          queryParams: { prompt: 'select_account' },
        },
      })
    } catch {
      localStorage.removeItem(OAUTH_FLAG)
      sessionStorage.removeItem(SESSION_KEY)
      setGoogleLoading(false)
    }
  }

  // ── Validaciones ────────────────────────────────────────────────────────────

  const validarPhone = (phone: string): string | undefined => {
    if (!phone.trim()) return 'Celular requerido'
    const d = phone.replace(/\D/g, '')
    const ok = (d.startsWith('3') && d.length === 10) ||
               (d.startsWith('57') && d.length === 12)
    if (!ok) return 'Celular colombiano (ej: 300 123 4567)'
  }

  const validarQuick = (): boolean => {
    const e: Partial<QuickForm> = {}
    if (!quickForm.name.trim())  e.name  = 'Nombre requerido'
    if (!quickForm.email.trim()) e.email = 'Email requerido'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(quickForm.email)) e.email = 'Email inválido'
    const pe = validarPhone(quickForm.phone)
    if (pe) e.phone = pe
    setErrorsQ(e)
    return Object.keys(e).length === 0
  }

  const validarStep1 = (): boolean => {
    const e: Partial<FormStep1> = {}
    if (!step1.name.trim())  e.name  = 'Nombre requerido'
    if (!step1.email.trim()) e.email = 'Email requerido'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(step1.email)) e.email = 'Email inválido'
    const pe = validarPhone(step1.phone)
    if (pe) e.phone = pe
    setErrors1(e)
    return Object.keys(e).length === 0
  }

  const validarStep2 = (): boolean => {
    const e: Partial<FormStep2> = {}
    if (!step2.departamento)       e.departamento = 'Selecciona un departamento'
    if (!step2.municipio.trim())   e.municipio    = 'Ciudad/municipio requerido'
    if (!step2.barrio.trim())      e.barrio       = 'Barrio requerido'
    if (!step2.numero_via.trim())    e.numero_via    = 'Número de vía requerido'
    if (!step2.numero_cruce.trim())  e.numero_cruce  = 'Número de cruce requerido'
    if (!step2.numero_puerta.trim()) e.numero_puerta = 'Número de puerta requerido'
    setErrors2(e)
    return Object.keys(e).length === 0
  }

  // ── Guardar lead ────────────────────────────────────────────────────────────

  const guardarLeadRapido = async () => {
    setIsSaving(true)
    setSaveError(null)
    try {
      const session = (await supabase.auth.getSession()).data.session
      await leadsApi.create({
        name:           quickForm.name,
        email:          quickForm.email,
        phone:          quickForm.phone,
        auth_provider:  googleUser ? 'google' : 'form',
        google_user_id: session?.user?.id || undefined,
      })
    } catch {
      setSaveError('No pudimos guardar tu información, pero puedes continuar por WhatsApp.')
    } finally {
      setIsSaving(false)
    }
  }

  const guardarLeadCompleto = async () => {
    setIsSaving(true)
    setSaveError(null)
    try {
      const session = (await supabase.auth.getSession()).data.session
      await leadsApi.create({
        name:             step1.name,
        email:            step1.email,
        phone:            step1.phone,
        tipo_documento:   step1.tipo_documento,
        numero_documento: step1.numero_documento,
        departamento:     step2.departamento,
        municipio:        step2.municipio,
        barrio:           step2.barrio,
        tipo_via:         step2.tipo_via,
        numero_via:       step2.numero_via,
        cuadrante:        step2.cuadrante || undefined,
        numero_cruce:     step2.numero_cruce,
        numero_puerta:    step2.numero_puerta,
        complemento:      step2.complemento || undefined,
        auth_provider:    googleUser ? 'google' : 'form',
        google_user_id:   session?.user?.id || undefined,
      })
    } catch {
      setSaveError('No pudimos guardar tu información, pero puedes continuar por WhatsApp.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleQuickSubmit = async () => {
    if (!validarQuick()) return
    await guardarLeadRapido()
    setStep(3)
  }

  const handleStep1Next = () => {
    if (validarStep1()) setStep(2)
  }

  const handleStep2Next = async () => {
    if (!validarStep2()) return
    await guardarLeadCompleto()
    setStep(3)
  }

  const handleReset = () => {
    setStep(1)
    setQuickForm(EMPTY_QUICK)
    setStep1(EMPTY_STEP1)
    setStep2(EMPTY_STEP2)
    setErrorsQ({})
    setErrors1({})
    setErrors2({})
    setSaveError(null)
    setProducto(null)
  }

  const qf = (field: keyof QuickForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuickForm(prev => ({ ...prev, [field]: e.target.value }))
    if (errorsQ[field]) setErrorsQ(prev => ({ ...prev, [field]: undefined }))
  }
  const s1 = (field: keyof FormStep1) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setStep1(prev => ({ ...prev, [field]: e.target.value }))
    if (errors1[field]) setErrors1(prev => ({ ...prev, [field]: undefined }))
  }
  const s2 = (field: keyof FormStep2) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setStep2(prev => ({ ...prev, [field]: e.target.value }))
    if (errors2[field]) setErrors2(prev => ({ ...prev, [field]: undefined }))
  }

  const inputClass = (err?: string) =>
    `w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-verano-primary transition-colors text-sm ${
      err ? 'border-red-400 bg-red-50' : 'border-gray-300'
    }`
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-1'
  const errClass   = 'text-xs text-red-500 mt-1'

  const direccionPreview = construirDireccion(step2)

  const handleGoogleSignOut = async () => {
    await supabase.auth.signOut()
    setGoogleUser(null)
    setShowOAuthBanner(false)
    setQuickForm(EMPTY_QUICK)
    setStep1(EMPTY_STEP1)
  }

  // Botón de Google: muestra cuenta activa con opción de cambiarla, o botón de login
  const googleButtonJSX = googleUser ? (
    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3">
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        <span className="text-sm text-green-800 font-medium truncate max-w-[220px]">{googleUser.email}</span>
      </div>
      <button
        type="button"
        onClick={handleGoogleSignOut}
        className="text-xs text-gray-500 hover:text-gray-700 underline flex-shrink-0 ml-2"
      >
        Cambiar cuenta
      </button>
    </div>
  ) : (
    <div>
      <button
        type="button"
        onClick={handleGoogleLogin}
        disabled={googleLoading}
        className="w-full flex items-center justify-center gap-3 border-2 border-gray-200 hover:border-gray-400 bg-white text-gray-700 font-semibold py-3 rounded-xl transition-all disabled:opacity-60"
      >
        {googleLoading ? (
          <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
        ) : (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        )}
        Continuar con Google
      </button>
      <p className="text-xs text-gray-400 text-center mt-2">
        Google verifica tu email automáticamente
      </p>
    </div>
  )

  // ── PASO 3 — Éxito ─────────────────────────────────────────────────────────

  if (step === 3) {
    const msgUrl = producto
      ? waUrl(mensajePedido(step1, step2, producto))
      : waUrl(mensajeRapido(quickForm))

    return (
      <section id="contacto" className="section-padding bg-verano-background">
        <div className="container-max">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">¡Datos registrados!</h2>
            <p className="text-gray-600 mb-6 text-sm">
              {producto
                ? 'Ahora envíanos el pedido por WhatsApp. Incluimos el producto, talla y tus datos automáticamente.'
                : 'Ahora contáctanos por WhatsApp. Incluimos tus datos de registro automáticamente.'}
            </p>
            {saveError && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">{saveError}</p>
            )}

            {/* Resumen */}
            <div className="bg-gray-50 rounded-xl p-4 text-left text-xs text-gray-600 mb-6 space-y-1">
              {producto && (
                <div className="mb-2 pb-2 border-b border-gray-200">
                  <p className="font-semibold text-gray-800 text-sm mb-1">Producto seleccionado</p>
                  <p><strong>Nombre:</strong> {producto.nombre}</p>
                  <p><strong>Talla:</strong> {producto.talla}</p>
                  <p><strong>Precio:</strong> ${producto.precio.toLocaleString('es-CO')}</p>
                </div>
              )}
              {producto ? (
                <>
                  <p><strong>Nombre:</strong> {step1.name}</p>
                  <p><strong>Celular:</strong> {step1.phone}</p>
                  <p><strong>Dirección:</strong> {construirDireccion(step2)}</p>
                  <p><strong>Ciudad:</strong> {step2.municipio}, {step2.departamento}</p>
                </>
              ) : (
                <>
                  <p><strong>Nombre:</strong> {quickForm.name}</p>
                  <p><strong>Celular:</strong> {quickForm.phone}</p>
                  <p><strong>Email:</strong> {quickForm.email}</p>
                </>
              )}
            </div>

            {/* Enlace directo — más confiable que window.open en todos los navegadores */}
            <a
              href={msgUrl}
              target="_blank"
              rel="noreferrer"
              className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-colors mb-3"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12c0 1.54.36 3 .97 4.29L2 22l6.29-.97c1.29.61 2.75.97 4.29.97 5.52 0 10-4.48 10-10S17.52 2 12 2zm0 18c-1.41 0-2.73-.36-3.88-.97l-.28-.15-2.89.44.44-2.89-.15-.28C4.36 14.73 4 13.41 4 12c0-4.41 3.59-8 8-8s8 3.59 8 8-3.59 8-8 8zm3.71-8.04c-.2-.1-1.18-.58-1.37-.65-.19-.07-.33-.1-.47.1-.13.2-.52.65-.63.78-.12.13-.23.15-.43.05-.2-.1-.84-.31-1.6-.99-.59-.53-.99-1.19-1.1-1.39-.12-.2-.01-.31.09-.41.09-.09.2-.23.3-.35.1-.12.13-.2.2-.33.07-.13.04-.25-.02-.35-.07-.1-.47-1.13-.64-1.55-.17-.41-.33-.36-.47-.37-.12 0-.25-.01-.39-.01-.13 0-.33.05-.5.25-.18.2-.66.64-.66 1.56 0 .92.68 1.81.77 1.94.09.13 1.27 1.93 3.06 2.71.43.19.76.3.99.38.43.13.82.11 1.13.07.35-.05 1.06-.43 1.21-.85.15-.42.15-.78.1-.85-.04-.07-.15-.11-.31-.2z" />
              </svg>
              {producto ? 'Enviar pedido por WhatsApp' : 'Consultar por WhatsApp'}
            </a>
            <button onClick={handleReset} className="text-sm text-gray-500 hover:text-gray-700 underline">
              {producto ? 'Registrar otro pedido' : 'Nuevo registro'}
            </button>
          </div>
        </div>
      </section>
    )
  }

  // ── Layout compartido ───────────────────────────────────────────────────────

  return (
    <section id="contacto" className="section-padding bg-verano-background">
      <div className="container-max">
        <div className="max-w-2xl mx-auto">

          {/* Encabezado */}
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
              {producto ? 'Realiza tu pedido' : 'Contáctanos'}
            </h2>
            <p className="text-gray-600">
              {producto
                ? 'Déjanos tus datos y te confirmamos disponibilidad y costo de envío.'
                : 'Déjanos tu número y te contactamos con nuestros productos y novedades.'}
            </p>
          </div>

          {/* Banner de producto seleccionado */}
          {producto && (
            <div className="flex items-center gap-3 bg-black text-white rounded-xl px-4 py-3 mb-6 text-sm">
              <span className="text-xl">🛒</span>
              <div className="flex-1">
                <p className="font-semibold">{producto.nombre}</p>
                <p className="text-gray-300 text-xs">Talla {producto.talla} · ${producto.precio.toLocaleString('es-CO')}</p>
              </div>
              <button
                onClick={() => setProducto(null)}
                className="text-gray-400 hover:text-white text-xs underline"
              >
                quitar
              </button>
            </div>
          )}

          {/* Indicador de pasos — solo con producto */}
          {producto && (
            <div className="flex items-center justify-center gap-3 mb-8">
              {[1, 2].map((n) => (
                <React.Fragment key={n}>
                  <div className={`flex items-center gap-2 text-sm font-semibold transition-colors ${
                    step >= n ? 'text-black' : 'text-gray-400'
                  }`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                      step > n   ? 'bg-green-500 text-white' :
                      step === n ? 'bg-black text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {step > n ? '✓' : n}
                    </div>
                    <span className="hidden sm:inline">
                      {n === 1 ? 'Datos personales' : 'Dirección de envío'}
                    </span>
                  </div>
                  {n < 2 && <div className={`flex-1 max-w-16 h-px ${step > 1 ? 'bg-green-500' : 'bg-gray-200'}`} />}
                </React.Fragment>
              ))}
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-10">

            {/* ── FORMULARIO RÁPIDO — sin producto ──────────────────────────── */}
            {!producto && step === 1 && (
              <div className="space-y-5">

                {/* Banner post-OAuth: nombre y email ya están, solo falta celular */}
                {showOAuthBanner && (
                  <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-start gap-3">
                    <span className="text-green-500 text-xl leading-none mt-0.5">✓</span>
                    <div>
                      <p className="text-green-800 font-semibold text-sm">
                        ¡Conectado como {googleUser?.email}!
                      </p>
                      <p className="text-green-700 text-xs mt-1">
                        Nombre y email ya están listos. Solo ingresa tu celular y haz clic en Registrarme.
                      </p>
                    </div>
                  </div>
                )}

                {/* Botón Google — se oculta si ya está autenticado */}
                {googleButtonJSX}

                {!googleUser && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400 font-medium">o completa manualmente</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                )}

                {/* Nombre */}
                <div>
                  <label className={labelClass}>Nombre completo *</label>
                  <input
                    type="text"
                    value={quickForm.name}
                    onChange={qf('name')}
                    placeholder="Tu nombre"
                    className={inputClass(errorsQ.name)}
                  />
                  {errorsQ.name && <p className={errClass}>{errorsQ.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className={labelClass}>Correo electrónico *</label>
                  <input
                    type="email"
                    value={quickForm.email}
                    onChange={qf('email')}
                    placeholder="tu@gmail.com"
                    className={inputClass(errorsQ.email)}
                  />
                  {errorsQ.email && <p className={errClass}>{errorsQ.email}</p>}
                </div>

                {/* Celular — ref para auto-focus después del OAuth */}
                <div>
                  <label className={`${labelClass} ${showOAuthBanner ? 'text-green-700' : ''}`}>
                    Celular Colombia *
                    {showOAuthBanner && (
                      <span className="ml-2 text-green-600 font-normal text-xs">← ingresa aquí</span>
                    )}
                  </label>
                  <div className="flex gap-2">
                    <span className="flex items-center px-3 bg-gray-100 border border-gray-300 rounded-lg text-sm text-gray-600 font-medium whitespace-nowrap">
                      🇨🇴 +57
                    </span>
                    <input
                      ref={phoneQuickRef}
                      type="tel"
                      value={quickForm.phone}
                      onChange={qf('phone')}
                      placeholder="300 123 4567"
                      className={`flex-1 ${inputClass(errorsQ.phone)} ${showOAuthBanner ? 'border-green-400 ring-1 ring-green-300' : ''}`}
                    />
                  </div>
                  {errorsQ.phone && <p className={errClass}>{errorsQ.phone}</p>}
                </div>

                <button
                  onClick={handleQuickSubmit}
                  disabled={isSaving}
                  className="w-full bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white font-bold py-4 rounded-xl transition-colors mt-2 flex items-center justify-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Guardando…
                    </>
                  ) : 'Registrarme y consultar por WhatsApp'}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Datos protegidos por la Ley 1581 de 2012 (Habeas Data Colombia)
                </p>
              </div>
            )}

            {/* ── PASO 1 — con producto ─────────────────────────────────────── */}
            {producto && step === 1 && (
              <div className="space-y-5">

                {showOAuthBanner && (
                  <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-start gap-3">
                    <span className="text-green-500 text-xl leading-none mt-0.5">✓</span>
                    <div>
                      <p className="text-green-800 font-semibold text-sm">
                        ¡Conectado como {googleUser?.email}!
                      </p>
                      <p className="text-green-700 text-xs mt-1">
                        Agrega tu celular y en el siguiente paso tu dirección de envío.
                      </p>
                    </div>
                  </div>
                )}

                {googleButtonJSX}

                {!googleUser && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400 font-medium">o completa manualmente</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                )}

                {/* Nombre */}
                <div>
                  <label className={labelClass}>Nombre completo *</label>
                  <input type="text" value={step1.name} onChange={s1('name')}
                    placeholder="Tu nombre"
                    className={inputClass(errors1.name)} />
                  {errors1.name && <p className={errClass}>{errors1.name}</p>}
                </div>

                {/* Documento */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass}>Tipo doc.</label>
                    <select value={step1.tipo_documento} onChange={s1('tipo_documento')}
                      className={inputClass()}>
                      {TIPOS_DOCUMENTO.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Número de documento</label>
                    <input type="text" value={step1.numero_documento} onChange={s1('numero_documento')}
                      placeholder="80123456"
                      className={inputClass()} />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className={labelClass}>Correo electrónico *</label>
                  <input type="email" value={step1.email} onChange={s1('email')}
                    placeholder="tu@gmail.com"
                    className={inputClass(errors1.email)} />
                  {errors1.email && <p className={errClass}>{errors1.email}</p>}
                </div>

                {/* Celular */}
                <div>
                  <label className={`${labelClass} ${showOAuthBanner ? 'text-green-700' : ''}`}>
                    Celular Colombia *
                    {showOAuthBanner && (
                      <span className="ml-2 text-green-600 font-normal text-xs">← ingresa aquí</span>
                    )}
                  </label>
                  <div className="flex gap-2">
                    <span className="flex items-center px-3 bg-gray-100 border border-gray-300 rounded-lg text-sm text-gray-600 font-medium whitespace-nowrap">
                      🇨🇴 +57
                    </span>
                    <input
                      ref={phone1Ref}
                      type="tel"
                      value={step1.phone}
                      onChange={s1('phone')}
                      placeholder="300 123 4567"
                      className={`flex-1 ${inputClass(errors1.phone)} ${showOAuthBanner ? 'border-green-400 ring-1 ring-green-300' : ''}`}
                    />
                  </div>
                  {errors1.phone && <p className={errClass}>{errors1.phone}</p>}
                </div>

                <button onClick={handleStep1Next}
                  className="w-full bg-black hover:bg-gray-800 text-white font-bold py-4 rounded-xl transition-colors mt-2">
                  Continuar → Dirección de envío
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Datos protegidos por la Ley 1581 de 2012 (Habeas Data Colombia)
                </p>
              </div>
            )}

            {/* ── PASO 2 — Dirección de envío ──────────────────────────────── */}
            {producto && step === 2 && (
              <div className="space-y-5">

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Departamento *</label>
                    <select value={step2.departamento} onChange={s2('departamento')}
                      className={inputClass(errors2.departamento)}>
                      <option value="">Seleccionar…</option>
                      {DEPARTAMENTOS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    {errors2.departamento && <p className={errClass}>{errors2.departamento}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Ciudad / Municipio *</label>
                    <input type="text" value={step2.municipio} onChange={s2('municipio')}
                      placeholder="Bogotá"
                      className={inputClass(errors2.municipio)} />
                    {errors2.municipio && <p className={errClass}>{errors2.municipio}</p>}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Barrio *</label>
                  <input type="text" value={step2.barrio} onChange={s2('barrio')}
                    placeholder="Fontibón"
                    className={inputClass(errors2.barrio)} />
                  {errors2.barrio && <p className={errClass}>{errors2.barrio}</p>}
                </div>

                <div>
                  <label className={labelClass}>Dirección *</label>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <select value={step2.tipo_via} onChange={s2('tipo_via')}
                      className={inputClass()}>
                      {TIPOS_VIA.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                    <input type="text" value={step2.numero_via} onChange={s2('numero_via')}
                      placeholder="Número de vía  (ej: 10)"
                      className={inputClass(errors2.numero_via)} />
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <select value={step2.cuadrante} onChange={s2('cuadrante')}
                      className={inputClass()}>
                      {CUADRANTES.map(c => (
                        <option key={c} value={c}>{c || 'Sin cuadrante'}</option>
                      ))}
                    </select>
                    <input type="text" value={step2.numero_cruce} onChange={s2('numero_cruce')}
                      placeholder="# Cruce (ej: 11)"
                      className={inputClass(errors2.numero_cruce)} />
                    <input type="text" value={step2.numero_puerta} onChange={s2('numero_puerta')}
                      placeholder="Puerta (ej: 18)"
                      className={inputClass(errors2.numero_puerta)} />
                  </div>
                  {(errors2.numero_via || errors2.numero_cruce || errors2.numero_puerta) && (
                    <p className={errClass}>Completa todos los campos de dirección</p>
                  )}
                  {direccionPreview && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm">
                      <span className="text-blue-500 font-semibold text-xs">Dirección: </span>
                      <span className="text-blue-800 font-mono">{direccionPreview}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Complemento <span className="text-gray-400 font-normal">(opcional)</span></label>
                  <input type="text" value={step2.complemento} onChange={s2('complemento')}
                    placeholder="Apto 302, Casa 5, Interior 2…"
                    className={inputClass()} />
                </div>

                <div className="flex gap-3 pt-2">
                  <button onClick={() => setStep(1)}
                    className="flex-1 border-2 border-gray-200 hover:border-gray-400 text-gray-700 font-semibold py-3 rounded-xl transition-all">
                    ← Volver
                  </button>
                  <button onClick={handleStep2Next} disabled={isSaving}
                    className="flex-2 flex-grow-[2] bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Guardando…
                      </>
                    ) : 'Confirmar y ver resumen'}
                  </button>
                </div>

                <p className="text-xs text-gray-400 text-center">
                  Formato estándar DANE Colombia · Ej: CL 10 SUR #11 - 18
                </p>
              </div>
            )}

          </div>
        </div>
      </div>
    </section>
  )
}

export default ContactForm
